import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import logo from '../../assets/logo.png';
import './PublicPages.css';
import './LandingPage.css';

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNavigate = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  return (
    <div className="landing-page">
      <SEO
        title="Hangrily — Can't Decide Where to Eat? Pick a Restaurant in 2 Minutes"
        description="Stop arguing about dinner. Hangrily is the #1 restaurant decision app — swipe through nearby restaurants solo, with your partner, or with up to 10 friends. Smarter than random pickers. Uses your preferences, real photos & ratings. Free to try."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Hangrily',
          description: 'The restaurant decision app that helps you choose where to eat using collaborative swiping.',
          url: 'https://hangrily.app',
          applicationCategory: 'LifestyleApplication',
          operatingSystem: 'Web Browser, iOS, Android',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }
        }}
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-icon">🔥</span>
            <span>10,000+ couples already using Hangrily</span>
          </div>
          <h1 className="hero-title">
            Stop Arguing.<br />
            <span className="gradient-text">Start Eating.</span>
          </h1>
          <p className="hero-subtitle">
            The Tinder-style app that ends restaurant indecision in <strong>under 2 minutes</strong>. 
            Swipe together, match instantly, and finally agree on where to eat.
          </p>
          <div className="hero-cta">
            <button 
              className="btn btn-primary btn-hero btn-glow"
              onClick={() => handleNavigate('/register')}
            >
              <span>Get Started Free</span>
              <span className="btn-arrow">→</span>
            </button>
            <button 
              className="btn btn-secondary btn-hero"
              onClick={() => handleNavigate('/try')}
            >
              <span>Try Without Account</span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
          <div className="hero-stats-row">
            <div className="stat-item">
              <div className="stat-number">2 min</div>
              <div className="stat-label">Avg. Decision Time</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">98%</div>
              <div className="stat-label">Match Success Rate</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">50K+</div>
              <div className="stat-label">Decisions Made</div>
            </div>
          </div>
        </div>
        <div className="hero-image">
          <div className="phone-mockup">
            <div className="phone-frame">
              <div className="phone-notch"></div>
              <div className="phone-screen">
                {/* App Header */}
                <div className="app-header">
                  <img src={logo} alt="Hangrily" className="app-header-logo" />
                  <div className="app-round-indicator">
                    <span className="round-dot active"></span>
                    <span className="round-dot active"></span>
                    <span className="round-dot"></span>
                  </div>
                  <div className="app-session-code">#A1B2C3</div>
                </div>
                
                {/* Swipe Card Stack */}
                <div className="swipe-cards-container">
                  <div className="swipe-card card-back-2"></div>
                  <div className="swipe-card card-back-1"></div>
                  <div className="swipe-card card-active">
                    <div className="card-image card-image-sushi">
                      <div className="card-image-overlay"></div>
                      <div className="card-badge">Round 2 of 3</div>
                      <div className="card-like-stamp">LIKE</div>
                    </div>
                    <div className="card-content">
                      <div className="card-title-row">
                        <h3 className="card-title">Sakura Sushi</h3>
                        <span className="card-rating">⭐ 4.8</span>
                      </div>
                      <div className="card-meta">
                        <span>🍣 Japanese</span>
                        <span>$$</span>
                        <span>📍 0.4 mi</span>
                      </div>
                      <div className="card-details">
                        <div className="detail-item">
                          <span className="detail-icon">🕐</span>
                          <span>Open · Closes 10pm</span>
                        </div>
                      </div>
                      <div className="card-tags">
                        <span className="tag">🔥 Trending</span>
                        <span className="tag">Date Night</span>
                        <span className="tag">Top Rated</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Swipe Actions */}
                <div className="swipe-actions">
                  <button className="action-btn action-pass">
                    <span className="action-icon">✕</span>
                  </button>
                  <button className="action-btn action-undo">
                    <span className="action-icon">↩</span>
                  </button>
                  <button className="action-btn action-like">
                    <span className="action-icon">♥</span>
                  </button>
                </div>
                
                {/* Partner Status */}
                <div className="partner-status">
                  <div className="partner-avatar">S</div>
                  <div className="partner-info">
                    <div className="partner-name">Sarah is swiping...</div>
                    <div className="partner-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{width: '70%'}}></div>
                      </div>
                    </div>
                  </div>
                  <div className="partner-count">7/10</div>
                </div>
              </div>
              <div className="phone-bottom-bar"></div>
            </div>
            <div className="phone-shadow"></div>
          </div>
          
          {/* Floating Elements */}
          <div className="floating-element float-1">
            <div className="float-card">
              <span className="float-icon">✓</span>
              <span className="float-text">It's a Match!</span>
            </div>
          </div>
          <div className="floating-element float-2">
            <div className="float-card">
              <span className="float-icon">⚡</span>
              <span className="float-text">2 min avg</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="section-container">
          <h2 className="section-title">Sound Familiar?</h2>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">😤</div>
              <h3>The Endless Loop</h3>
              <p>"Where do you want to eat?" "I don't know, where do YOU want to eat?"</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">⏰</div>
              <h3>Wasted Time</h3>
              <p>30 minutes of scrolling through restaurant apps, still can't decide</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">😩</div>
              <h3>Decision Fatigue</h3>
              <p>Too many options, everyone has different preferences</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">🤷</div>
              <h3>The Veto Game</h3>
              <p>One person suggests, the other vetoes. Repeat until hangry.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <div className="section-container">
          <h2 className="section-title">The Hangrily Solution</h2>
          <p className="section-subtitle">
            Swipe. Match. Eat. It's that simple.
          </p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h3>AI Smart Search</h3>
              <p>Tell us what you want in plain English. "I want spicy tacos under $15" — AI finds the perfect match.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">👆</div>
              <h3>Swipe Together</h3>
              <p>Both partners swipe on nearby restaurants. Like Tinder, but for food.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">❤️</div>
              <h3>Instant Matches</h3>
              <p>Our algorithm finds restaurants you BOTH love in 3 quick rounds.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Smart Filters</h3>
              <p>Set cuisine, price, mood, and distance preferences before you start.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">👥</div>
              <h3>Solo, Couple, or Group</h3>
              <p>Works for date night, family dinners, or just you being indecisive.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="section-container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Set Your Preferences</h3>
                <p>Choose cuisine type, price range, distance, and mood. We'll find restaurants that match.</p>
              </div>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Swipe on Restaurants</h3>
                <p>See restaurant cards with photos, ratings, and details. Swipe right to like, left to pass.</p>
              </div>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Get Your Match</h3>
                <p>In 3 rounds or less, we'll show you the perfect restaurant everyone agrees on.</p>
              </div>
            </div>
          </div>
          <button 
            className="btn btn-primary btn-lg"
            onClick={() => navigate('/try')}
          >
            Try It Free Now
          </button>
        </div>
      </section>

      {/* Modes Section */}
      <section className="modes-section">
        <div className="section-container">
          <h2 className="section-title">Perfect for Every Situation</h2>
          <div className="modes-grid">
            <div className="mode-card">
              <div className="mode-icon">🍽️</div>
              <h3>Solo Mode</h3>
              <p>Can't decide on your own? Let Hangrily help you narrow down your options when decision fatigue hits.</p>
              <ul className="mode-features">
                <li>✓ Quick 3-round decision process</li>
                <li>✓ Filter by your exact preferences</li>
                <li>✓ Save your favorite spots</li>
              </ul>
              <Link to="/where-should-i-eat" className="mode-link" onClick={() => window.scrollTo(0, 0)}>Learn more →</Link>
            </div>
            <div className="mode-card mode-featured">
              <div className="mode-badge">Most Popular</div>
              <div className="mode-icon">💑</div>
              <h3>Couple Mode</h3>
              <p>End the "where should we eat?" argument. Both partners swipe, and we find restaurants you both love.</p>
              <ul className="mode-features">
                <li>✓ Link your partner's account</li>
                <li>✓ Swipe simultaneously</li>
                <li>✓ Only see mutual matches</li>
                <li>✓ Send hunger nudges</li>
              </ul>
              <Link to="/couples-restaurant-picker" className="mode-link" onClick={() => window.scrollTo(0, 0)}>Learn more →</Link>
            </div>
            <div className="mode-card">
              <div className="mode-icon">👥</div>
              <h3>Group Mode</h3>
              <p>Planning dinner with friends? Get everyone's input and find a restaurant the whole group agrees on.</p>
              <ul className="mode-features">
                <li>✓ Up to 10 participants</li>
                <li>✓ Democratic voting system</li>
                <li>✓ Share session codes easily</li>
              </ul>
              <Link to="/group-restaurant-decision" className="mode-link" onClick={() => window.scrollTo(0, 0)}>Learn more →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="testimonials-section">
        <div className="section-container">
          <h2 className="section-title">What People Are Saying</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="stars">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-text">
                "This app literally saved our relationship. We used to spend 45 minutes arguing about where to eat. Now it takes 2 minutes!"
              </p>
              <p className="testimonial-author">- Sarah & Mike, Chicago</p>
            </div>
            <div className="testimonial-card">
              <div className="stars">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-text">
                "Game changer for group dinners. No more endless group chats trying to pick a restaurant. Everyone swipes, we get a match, done."
              </p>
              <p className="testimonial-author">- Jessica, New York</p>
            </div>
            <div className="testimonial-card">
              <div className="stars">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-text">
                "Even when I'm eating alone, I use this. It's like having a friend help you decide when you're too hungry to think straight."
              </p>
              <p className="testimonial-author">- David, San Francisco</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Ready to End Restaurant Indecision?</h2>
          <p className="cta-subtitle">
            Join thousands of users who've stopped arguing and started eating better.
          </p>
          <button 
            className="btn btn-primary btn-hero"
            onClick={() => navigate('/try')}
          >
            Try It Free — No Account Required
          </button>
          <p className="cta-note">
            ✓ Free to start  ✓ No ads  ✓ Works on any device
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
